"use client";

import { useState, type ReactNode } from "react";
import { CalendarDays, UserRound } from "lucide-react";
import type { FormState } from "@/modules/shared/forms";
import { BookingFacts } from "./BookingFacts";
import { StatusColorBadge } from "./BookingStatusBadge";
import { STATUS_COLOR, type StatusTone } from "./bookingStatus";
import { Banner } from "./ui";

/**
 * ConsumerBookingsScreen (docs/ux/screens/ConsumerBookingsScreen.md), shared by
 * both frontends (R22 T13).
 *
 * **Three commands and a link renderer arrive as props**, and nothing else
 * about this changed. Withdraw, cancel and review are API calls, and how they
 * travel has never been a rule; injecting them is what keeps this file free of
 * transport entirely, which is how it served two hosts through the R22 cutover
 * and how a test renders it now. Everything here that *is* a rule stays: which actions a
 * booking offers comes from the flags the server computed
 * (`canWithdraw`/`canCancel`/`canReschedule`/`canReview`), and this component
 * only renders them. It cannot grant an action the server did not.
 */

export interface ConsumerBookingView {
  id: string;
  serviceName: string;
  businessName: string;
  /** Null while unassigned (H23), the clinic assigns at approval. */
  practitionerName: string | null;
  whenLabel: string;
  priceDisplay: string;
  /** Service Location (H24): "In-Clinic" or "At-Home". */
  locationLabel: string;
  /** At-Home delivery address (H24); null for In-Clinic. */
  homeAddress: string | null;
  statusLabel: string;
  statusTone: StatusTone;
  /** Requested → can withdraw anytime (H8). */
  canWithdraw: boolean;
  /** Approved & outside the 1-hour Window. */
  canCancel: boolean;
  /** Approved & outside the 1-hour Window: they may pick another time (H50). */
  canReschedule: boolean;
  /**
   * The clinic's suggested time, preformatted, when one is still worth
   * answering (H49). Null otherwise, including once it has lapsed.
   */
  suggestedWhenLabel: string | null;
  /** Terminal states show no actions. */
  isTerminal: boolean;
  /** Completed & unlocked (15 min past end, H13) & not yet reviewed. */
  canReview: boolean;
  /** The rating this consumer already left, or null (one per booking, H13). */
  reviewedRating: number | null;
}

/**
 * What the host is asked to do. Each resolves to a `FormState`, so a refusal is
 * rendered by the same copy map on both sides, and each is told which booking.
 */
export interface ConsumerBookingActions {
  withdraw: (bookingId: string) => Promise<FormState>;
  cancel: (bookingId: string) => Promise<FormState>;
  review: (
    bookingId: string,
    rating: number,
    body: string,
  ) => Promise<FormState>;
  /** An in-app link. The router's, injected so this file imports no router. */
  Link: (props: {
    href: string;
    className?: string;
    children: ReactNode;
  }) => ReactNode;
}

export function ConsumerBookings({
  bookings,
  actions,
}: {
  bookings: ConsumerBookingView[];
  actions: ConsumerBookingActions;
}) {
  const { Link } = actions;

  if (bookings.length === 0) {
    return (
      <div className="rounded-xl border border-line bg-surface p-10 text-center shadow-sm">
        <span
          className="mx-auto mb-4 flex size-12 items-center justify-center rounded-xl bg-accent/10 text-accent"
          aria-hidden
        >
          <CalendarDays className="size-6" strokeWidth={1.6} />
        </span>
        <p className="font-semibold text-ink">No bookings yet</p>
        <p className="mx-auto mt-1 max-w-xs text-sm text-muted">
          Find a clinic near you and request a time. They confirm before it is
          booked.
        </p>
        <Link
          href="/search"
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-on-primary transition hover:brightness-110"
        >
          Find care
        </Link>
      </div>
    );
  }

  // Split on the terminal flag the view already carries: anything still live is
  // something the person may need to act on, anything terminal is history. One
  // undifferentiated list made a cancelled booking from March look exactly like
  // tomorrow morning's appointment.
  const upcoming = bookings.filter((b) => !b.isTerminal);
  const past = bookings.filter((b) => b.isTerminal);

  return (
    <div className="space-y-8">
      {upcoming.length > 0 && (
        <Section
          title="Upcoming"
          count={upcoming.length}
          bookings={upcoming}
          actions={actions}
        />
      )}
      {past.length > 0 && (
        <Section
          title="Past"
          count={past.length}
          bookings={past}
          actions={actions}
        />
      )}
    </div>
  );
}

function Section({
  title,
  count,
  bookings,
  actions,
}: {
  title: string;
  count: number;
  bookings: ConsumerBookingView[];
  actions: ConsumerBookingActions;
}) {
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-muted">
        {title}
        <span className="rounded-md bg-line px-2 py-0.5 font-mono text-xs text-body">
          {count}
        </span>
      </h2>
      <ul className="space-y-3">
        {bookings.map((booking) => (
          <BookingRow key={booking.id} booking={booking} actions={actions} />
        ))}
      </ul>
    </section>
  );
}

function BookingRow({
  booking,
  actions,
}: {
  booking: ConsumerBookingView;
  actions: ConsumerBookingActions;
}) {
  const { Link } = actions;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"withdraw" | "cancel" | null>(null);

  // A refused command leaves the card exactly as it was and says why. Nothing
  // is removed optimistically: a booking that vanishes and comes back is worse
  // than one that takes a moment, and these two are irreversible.
  const run = (kind: "withdraw" | "cancel") => {
    setBusy(kind);
    setError(null);
    const command = kind === "withdraw" ? actions.withdraw : actions.cancel;
    void command(booking.id)
      .then((state) => setError(state.formError ?? null))
      .finally(() => setBusy(null));
  };

  return (
    <li
      className={`rounded-xl border border-line border-l-4 bg-surface p-5 shadow-sm ${STATUS_COLOR[booking.statusTone].stripe}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* When leads. It is the thing a person opens this screen to check,
              and it used to be the third of four lines in the same grey. */}
          <p className="inline-flex items-center gap-2 rounded-lg bg-accent/10 px-2.5 py-1 font-semibold text-ink">
            <CalendarDays className="size-4 text-accent" aria-hidden />
            {booking.whenLabel}
          </p>
          <p className="mt-2 text-sm">
            <span className="font-semibold text-ink">{booking.serviceName}</span>
            <span className="text-muted"> · </span>
            <span className="text-accent">{booking.businessName}</span>
          </p>
          {/* H45: the practitioner is revealed at approval and not before, so
              this line has three cases rather than two. A name means the clinic
              has assigned somebody. No name on a live booking means it has not
              yet. No name on a finished one means nobody ever will, and saying
              "to be assigned" there would be a promise about a booking that is
              over. The server decides which; this only renders it. */}
          {booking.practitionerName ? (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-body">
              <UserRound className="size-3.5 text-teal" aria-hidden />
              {booking.practitionerName}
            </p>
          ) : (
            !booking.isTerminal && (
              <p className="mt-1 flex items-center gap-1.5 text-sm text-muted">
                <UserRound className="size-3.5 text-teal" aria-hidden />
                <span className="italic">Practitioner to be assigned</span>
              </p>
            )
          )}
          <BookingFacts
            priceDisplay={booking.priceDisplay}
            locationLabel={booking.locationLabel}
            atHome={booking.homeAddress !== null}
          />
          {booking.homeAddress && (
            <p className="mt-1.5 text-sm text-muted">{booking.homeAddress}</p>
          )}
        </div>
        <StatusColorBadge tone={booking.statusTone} label={booking.statusLabel} />
      </div>

      {error && (
        <div className="mt-3">
          <Banner>{error}</Banner>
        </div>
      )}

      {/* The clinic asked to move this one (H49). It sits above the actions and
          in the accent colour because it is the only thing on this card that
          is waiting on the reader, and the booking above it is still true
          until they answer. */}
      {booking.suggestedWhenLabel && (
        <div className="mt-3 rounded-lg border border-accent/40 bg-accent/5 p-3">
          <p className="text-sm font-semibold text-ink">
            {booking.businessName} suggested {booking.suggestedWhenLabel}
          </p>
          <p className="mt-0.5 text-xs text-muted">
            Your original time stands until you answer.
          </p>
          <Link
            href={`/bookings/${booking.id}/suggestion`}
            className="mt-2 inline-flex rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:brightness-110"
          >
            Confirm or reject
          </Link>
        </div>
      )}

      {(booking.canWithdraw || booking.canCancel || booking.canReschedule) && (
        <div className="mt-3 flex flex-wrap gap-3 border-t border-line pt-3">
          {booking.canWithdraw && (
            <ActionButton
              label="Withdraw request"
              pending="Withdrawing…"
              busy={busy === "withdraw"}
              disabled={busy !== null}
              onClick={() => run("withdraw")}
              danger
            />
          )}
          {/* H50: picking another time is a page rather than a button, because
              it needs the clinic's open hours to choose from. */}
          {booking.canReschedule && (
            <Link
              href={`/bookings/${booking.id}/reschedule`}
              className="rounded-lg border border-line px-3 py-1.5 text-sm font-semibold text-body transition hover:border-accent"
            >
              Change time
            </Link>
          )}
          {booking.canCancel && (
            <ActionButton
              label="Cancel booking"
              pending="Cancelling…"
              busy={busy === "cancel"}
              disabled={busy !== null}
              onClick={() => run("cancel")}
              danger
            />
          )}
        </div>
      )}

      {booking.statusTone === "success" && !booking.canCancel && (
        <p className="mt-3 border-t border-line pt-3 text-xs text-muted">
          Cancellations close 1 hour before the start, contact the business
          directly after that.
        </p>
      )}

      {booking.reviewedRating !== null && (
        <div className="mt-3 flex items-center gap-2 border-t border-line pt-3">
          <span className="text-sm text-muted">You rated</span>
          <StarRow value={booking.reviewedRating} />
        </div>
      )}

      {booking.canReview && booking.reviewedRating === null && (
        <ReviewForm bookingId={booking.id} submit={actions.review} />
      )}
    </li>
  );
}

/** Read-only star row (whole stars). */
function StarRow({ value }: { value: number }) {
  return (
    <span className="text-base leading-none" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= value ? "text-teal" : "text-line"} aria-hidden="true">
          ★
        </span>
      ))}
    </span>
  );
}

/**
 * Inline review submission on a completed booking (docs/ux/screens/
 * ConsumerBookingsScreen.md). Rating (1–5) required; text optional (H18). On
 * success the screen re-reads /bookings and this row re-renders as
 * "You rated ★N" (one per booking, H13).
 */
function ReviewForm({
  bookingId,
  submit,
}: {
  bookingId: string;
  submit: ConsumerBookingActions["review"];
}) {
  const [state, setState] = useState<FormState>({});
  const [pending, setPending] = useState(false);
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState("");

  if (state.success) {
    // The row re-reads and renders "You rated ★N" once the list refreshes; this
    // is what it says in the moment between, so the form does not sit there
    // looking as though nothing happened.
    return (
      <p className="mt-3 border-t border-line pt-3 text-sm text-muted">
        Thanks, your review is saved.
      </p>
    );
  }

  return (
    <form
      className="mt-3 border-t border-line pt-3"
      onSubmit={(event) => {
        event.preventDefault();
        setPending(true);
        setState({});
        void submit(bookingId, rating, body)
          .then(setState)
          .finally(() => setPending(false));
      }}
    >
      <p className="mb-2 text-sm font-medium text-ink">Leave a review</p>

      <div role="radiogroup" aria-label="Rating" className="mb-2 flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            aria-pressed={rating === n}
            className={`text-2xl leading-none transition-transform hover:scale-110 ${
              n <= rating ? "text-teal" : "text-line"
            }`}
          >
            ★
          </button>
        ))}
      </div>

      <textarea
        name="body"
        rows={2}
        maxLength={2000}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder="Share how it went (optional)"
        className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-body outline-none focus:border-accent"
      />

      {state.formError && (
        <div className="mt-2">
          <Banner>{state.formError}</Banner>
        </div>
      )}

      <div className="mt-2">
        <button
          type="submit"
          disabled={pending}
          className="inline-flex min-h-11 items-center justify-center rounded-lg bg-primary px-4 py-2.5 font-semibold text-on-primary transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {pending ? "Submitting…" : "Submit review"}
        </button>
      </div>
    </form>
  );
}

function ActionButton({
  label,
  pending,
  busy,
  disabled,
  onClick,
  danger,
}: {
  label: string;
  pending: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-60 ${
        danger
          ? "border border-danger/40 text-danger hover:bg-danger/10"
          : "border border-line text-body hover:border-accent"
      }`}
    >
      {busy ? pending : label}
    </button>
  );
}
